import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Navigation, Watch, Heart, MapPin, Clock, AlertTriangle, 
  Phone, Play, CheckCircle, Battery, Wifi, Activity, Bell,
  Route, User, ExternalLink, RefreshCw, Shield, Pill
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GPSSmartWatch = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [patients, setPatients] = useState([]);
  const [route, setRoute] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceStats, setDeviceStats] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [alertStats, setAlertStats] = useState({});
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [alertResponse, setAlertResponse] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboard(),
        fetchPatients(),
        fetchDevices(),
        fetchAlerts()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    const response = await fetch(`${API_URL}/api/phase52/dashboard`);
    const data = await response.json();
    setDashboard(data);
  };

  const fetchPatients = async () => {
    const response = await fetch(`${API_URL}/api/phase52/navigation/patients`);
    const data = await response.json();
    setPatients(data.patients || []);
  };

  const fetchDevices = async () => {
    const response = await fetch(`${API_URL}/api/phase52/smartwatch/devices`);
    const data = await response.json();
    setDevices(data.devices || []);
    setDeviceStats(data.statistics || {});
  };

  const fetchAlerts = async () => {
    const response = await fetch(`${API_URL}/api/phase52/smartwatch/alerts`);
    const data = await response.json();
    setAlerts(data.alerts || []);
    setAlertStats(data.statistics || {});
  };

  const calculateRoute = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase52/navigation/route`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ optimize: true })
      });
      const data = await response.json();
      setRoute(data);
      toast.success('Route optimized!');
    } catch (error) {
      toast.error('Failed to calculate route');
    }
  };

  const startNavigation = async (patientId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase52/navigation/start/${patientId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await response.json();
      if (data.navigation?.maps_url) {
        window.open(data.navigation.maps_url, '_blank');
      }
      toast.success('Navigation started!');
    } catch (error) {
      toast.error('Failed to start navigation');
    }
  };

  const acknowledgeAlert = async () => {
    if (!selectedAlert) return;
    try {
      await fetch(`${API_URL}/api/phase52/smartwatch/alerts/${selectedAlert.id}/acknowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: alertResponse })
      });
      toast.success('Alert acknowledged');
      setShowAlertDialog(false);
      setAlertResponse('');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-amber-100 text-amber-800';
      case 'low': return 'bg-blue-100 text-blue-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'bg-green-100 text-green-800';
      case 'low_battery': return 'bg-amber-100 text-amber-800';
      case 'disconnected': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading GPS & SmartWatch data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-100 rounded-xl">
                <Navigation className="h-8 w-8 text-teal-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-800" data-testid="gps-title">GPS & Smart Watch</h1>
                <p className="text-slate-500">Navigation, route optimization, and patient monitoring</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchAllData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-4 gap-2 bg-white p-1 rounded-lg shadow-sm">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <Activity className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="navigation" data-testid="tab-navigation">
              <Navigation className="h-4 w-4 mr-2" />
              Navigation
            </TabsTrigger>
            <TabsTrigger value="smartwatch" data-testid="tab-smartwatch">
              <Watch className="h-4 w-4 mr-2" />
              Smart Watch
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">
              <Bell className="h-4 w-4 mr-2" />
              Alerts
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {dashboard && (
              <>
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-500">Assigned Patients</p>
                          <p className="text-3xl font-bold text-slate-800">{dashboard.navigation.assigned_patients}</p>
                        </div>
                        <User className="h-10 w-10 text-teal-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-red-700">Critical Visits</p>
                          <p className="text-3xl font-bold text-red-600">{dashboard.navigation.critical_visits}</p>
                        </div>
                        <AlertTriangle className="h-10 w-10 text-red-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700">Active Devices</p>
                          <p className="text-3xl font-bold text-green-600">{dashboard.smartwatch.active_devices}</p>
                        </div>
                        <Watch className="h-10 w-10 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-amber-700">Pending Alerts</p>
                          <p className="text-3xl font-bold text-amber-600">{dashboard.smartwatch.pending_alerts}</p>
                        </div>
                        <Bell className="h-10 w-10 text-amber-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Today's Schedule */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-teal-600" />
                      Today's Schedule
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard.today_schedule.map((visit, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[60px]">
                              <p className="font-bold text-teal-600">{visit.time}</p>
                            </div>
                            <div>
                              <p className="font-medium">{visit.patient_name}</p>
                              <p className="text-sm text-slate-500">{visit.visit_type}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getPriorityColor(visit.priority)}>{visit.priority}</Badge>
                            <Badge variant="outline" className={
                              visit.status === 'completed' ? 'bg-green-50 text-green-700' :
                              visit.status === 'in_progress' ? 'bg-blue-50 text-blue-700' :
                              'bg-slate-50 text-slate-700'
                            }>
                              {visit.status.replace('_', ' ')}
                            </Badge>
                            {visit.status === 'scheduled' && (
                              <Button size="sm" onClick={() => startNavigation(visit.patient_id)}>
                                <Navigation className="h-3 w-3 mr-1" />
                                Navigate
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Pending Alerts */}
                {dashboard.recent_alerts.length > 0 && (
                  <Card className="border-l-4 border-l-amber-500">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-amber-500" />
                        Pending Alerts
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {dashboard.recent_alerts.map((alert, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                            <div>
                              <p className="font-medium">{alert.patient_name}</p>
                              <p className="text-sm text-amber-700">{alert.alert_type.replace('_', ' ')}</p>
                            </div>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setSelectedAlert(alert); setShowAlertDialog(true); }}
                            >
                              Respond
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Navigation Tab */}
          <TabsContent value="navigation" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Patient Locations</h2>
              <Button onClick={calculateRoute} data-testid="optimize-route-btn">
                <Route className="h-4 w-4 mr-2" />
                Optimize Route
              </Button>
            </div>

            {/* Route Summary */}
            {route && (
              <Card className="bg-teal-50 border-teal-200">
                <CardHeader>
                  <CardTitle className="text-teal-800">Optimized Route</CardTitle>
                  <CardDescription>
                    {route.summary.total_stops} stops • {route.summary.total_distance_miles} miles • 
                    ~{route.summary.total_drive_time_minutes} min drive time • Traffic: {route.summary.traffic_conditions}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {route.route.map((stop, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                        <div className="w-8 h-8 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{stop.patient_name}</p>
                          <p className="text-sm text-slate-500">{stop.address}</p>
                        </div>
                        <div className="text-right text-sm">
                          <p className="font-medium text-teal-600">ETA: {stop.eta}</p>
                          <p className="text-slate-500">{stop.distance_miles} mi • {stop.drive_time_minutes} min</p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => startNavigation(stop.patient_id)}>
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Patient Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              {patients.map(patient => (
                <Card key={patient.patient_id} className="hover:shadow-lg transition-shadow" data-testid={`patient-${patient.patient_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{patient.patient_name}</CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {patient.address.full}
                        </CardDescription>
                      </div>
                      <Badge className={getPriorityColor(patient.priority)}>{patient.priority}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {patient.access_notes && (
                      <div className="p-2 bg-blue-50 rounded text-sm text-blue-800 mb-3">
                        <strong>Access:</strong> {patient.access_notes}
                      </div>
                    )}
                    {patient.gate_code && (
                      <div className="p-2 bg-amber-50 rounded text-sm text-amber-800 mb-3">
                        <strong>Gate Code:</strong> {patient.gate_code}
                      </div>
                    )}
                    {patient.special_instructions && (
                      <p className="text-sm text-slate-600 mb-3">{patient.special_instructions}</p>
                    )}
                    <Button className="w-full" onClick={() => startNavigation(patient.patient_id)}>
                      <Navigation className="h-4 w-4 mr-2" />
                      Navigate
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* SmartWatch Tab */}
          <TabsContent value="smartwatch" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="pt-6 text-center">
                  <Wifi className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-600">{deviceStats.connected}</p>
                  <p className="text-sm text-green-700">Connected</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6 text-center">
                  <Battery className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-amber-600">{deviceStats.low_battery}</p>
                  <p className="text-sm text-amber-700">Low Battery</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6 text-center">
                  <Watch className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-slate-600">{deviceStats.total}</p>
                  <p className="text-sm text-slate-500">Total Devices</p>
                </CardContent>
              </Card>
            </div>

            {/* Device Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {devices.map(device => (
                <Card key={device.device_id} className="hover:shadow-lg transition-shadow" data-testid={`device-${device.device_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-base">{device.patient_name}</CardTitle>
                        <CardDescription>{device.device_type}</CardDescription>
                      </div>
                      <Badge className={getStatusColor(device.status)}>{device.status.replace('_', ' ')}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 mb-3">
                      <Battery className={`h-4 w-4 ${device.battery_level < 20 ? 'text-red-500' : device.battery_level < 50 ? 'text-amber-500' : 'text-green-500'}`} />
                      <span className="text-sm">{device.battery_level}%</span>
                      <span className="text-xs text-slate-400 ml-auto">
                        Last sync: {new Date(device.last_sync).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {device.features_enabled.map(feature => (
                        <Badge key={feature} variant="outline" className="text-xs">
                          {feature === 'heart_rate' && <Heart className="h-3 w-3 mr-1" />}
                          {feature === 'fall_detection' && <Shield className="h-3 w-3 mr-1" />}
                          {feature === 'medication_reminders' && <Pill className="h-3 w-3 mr-1" />}
                          {feature === 'sos' && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {feature.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold">{alertStats.total}</p>
                  <p className="text-sm text-slate-500">Total</p>
                </CardContent>
              </Card>
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-amber-600">{alertStats.pending}</p>
                  <p className="text-sm text-amber-700">Pending</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-red-600">{alertStats.critical}</p>
                  <p className="text-sm text-red-700">Critical</p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-6 text-center">
                  <p className="text-2xl font-bold text-orange-600">{alertStats.high}</p>
                  <p className="text-sm text-orange-700">High</p>
                </CardContent>
              </Card>
            </div>

            {/* Alerts List */}
            <Card>
              <CardHeader>
                <CardTitle>All Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {alerts.map(alert => (
                    <div key={alert.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${
                          alert.severity === 'critical' ? 'bg-red-100' :
                          alert.severity === 'high' ? 'bg-orange-100' :
                          alert.severity === 'medium' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          {alert.alert_type === 'fall_detected' && <Shield className="h-5 w-5 text-red-600" />}
                          {alert.alert_type === 'sos_triggered' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                          {alert.alert_type === 'low_heart_rate' && <Heart className="h-5 w-5 text-amber-600" />}
                          {alert.alert_type === 'low_battery' && <Battery className="h-5 w-5 text-blue-600" />}
                        </div>
                        <div>
                          <p className="font-medium">{alert.patient_name}</p>
                          <p className="text-sm text-slate-500">{alert.alert_type.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-slate-400">{new Date(alert.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                        <Badge variant="outline" className={
                          alert.status === 'resolved' ? 'bg-green-50 text-green-700' :
                          alert.status === 'acknowledged' ? 'bg-blue-50 text-blue-700' :
                          'bg-amber-50 text-amber-700'
                        }>
                          {alert.status}
                        </Badge>
                        {alert.status === 'pending' && (
                          <Button 
                            size="sm"
                            onClick={() => { setSelectedAlert(alert); setShowAlertDialog(true); }}
                          >
                            Respond
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Alert Response Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respond to Alert</DialogTitle>
            <DialogDescription>
              {selectedAlert?.alert_type.replace(/_/g, ' ')} for {selectedAlert?.patient_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm"><strong>Alert Type:</strong> {selectedAlert?.alert_type.replace(/_/g, ' ')}</p>
              <p className="text-sm"><strong>Severity:</strong> {selectedAlert?.severity}</p>
              <p className="text-sm"><strong>Time:</strong> {selectedAlert?.timestamp && new Date(selectedAlert.timestamp).toLocaleString()}</p>
            </div>
            <div>
              <Label>Response Notes</Label>
              <Textarea
                value={alertResponse}
                onChange={(e) => setAlertResponse(e.target.value)}
                placeholder="Describe the action taken..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>Cancel</Button>
            <Button onClick={acknowledgeAlert} data-testid="acknowledge-alert-btn">
              <CheckCircle className="h-4 w-4 mr-2" />
              Acknowledge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GPSSmartWatch;
